import { catchError, flatMap } from 'rxjs/operators';
import { Component, OnInit, Input, ViewChild } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import {
  Client,
  DEFAULT_HELP_INSTRUCTIONS,
  ProductGroup,
} from '@app/core/models/client';
import { Journey, JourneyContent } from '@app/core/models/journey';
import { JourneyService } from '@app/core/services/journey.service';
import { Background } from '@app/core/utils/background';
import { PdfService } from '@app/core/services/pdf.service';
import { WirePreviewService } from '@app/core/services/wire-preview.service';
import { MessageDialogComponent } from '@app/shared/message-dialog/message-dialog.component';
import { ScreenSelectionDialogComponent } from '@app/shared/screen-selection-dialog/screen-selection-dialog.component';
import { ControlTagsService } from '@app/core/services/control-tags.service';
import * as _ from 'lodash';
import { MessageClass, SmsClass, WireClass } from '@app/core/models/message';
import { Action } from '@app/core/models/action';
import { FeatureService } from '@app/core/services/feature.service';
import { FeatureFlags } from '@app/core/models/feature-flags';
import {
  DEFAULT_ADDITIONAL_SCREENS,
  ScreenOption,
} from './additional-screens-list';
import { SessionService } from '@app/security/session.service';
import { ClientService } from '@app/core/services/client.service';
import { T2eService } from '@app/core/services/t2e.service';
import { T2EKeyword } from '@app/t2e-mgmt/types/t2e-keyword';
import { NgClass, AsyncPipe, DatePipe } from '@angular/common';
import { PhoneComponent } from '../../shared/phone/phone.component';
import { JourneyArrowComponent } from '../../cx-builder/journey-arrow/journey-arrow.component';
import { NewGenericPhoneComponent } from '../../shared/new-generic-phone/new-generic-phone.component';
import { AboutRelayPreviewReactContainerComponent } from '../../shared/phone/about-relay-preview-react-container/about-relay-preview-react-container.component';
import { PrivacyPolicyPreviewReactContainerComponent } from '../../shared/phone/privacy-policy-preview-react-container/privacy-policy-preview-react-container.component';
import { TermsAndConditionsPreviewReactContainerComponent } from '../../shared/phone/terms-and-conditions-preview-react-container/terms-and-conditions-preview-react-container.component';
import { VerificationPreviewComponent } from '../../shared/phone/verification-preview/verification-preview.component';
import {
  resolveVerificationPreviewProps,
  VerificationPreviewProps,
} from '../../shared/phone/verification-preview/resolve-verification-preview-props';
import {
  resolvePasswordPagesPreviewProps,
  PasswordPagesPreviewProps,
} from '../../shared/phone/password-pages-preview/resolve-password-pages-preview-props';
import { SetPasswordPagePreviewReactContainerComponent } from '@app/client-config/product-group/components/set-password-page-preview-react-container/set-password-page-preview-react-container.component';
import { PasswordPagePreviewComponent } from '@app/shared/phone/password-pages-preview/password-page-preview.component';
import { MessageDialogComponent as MessageDialogComponent_1 } from '../../shared/message-dialog/message-dialog.component';
import { ScreenSelectionDialogComponent as ScreenSelectionDialogComponent_1 } from '../../shared/screen-selection-dialog/screen-selection-dialog.component';
import { TimerPopupComponent } from '../../src/app/shared/timer-popup/timer-popup.component';

const defaultPageWidth = 1224; /** a4 width in px, used in @link {https://github.com/relaynetwork/rn-v3-portal/blob/7fbe8bd0f5b5666eba1890d8def9036ec5eee350/portal/src/app/preview/journey-pdf-preview/journey-pdf-preview.component.scss#L5} */
const defaultPageHeight = 1585; /** a4 height in px, used in @link {https://github.com/relaynetwork/rn-v3-portal/blob/7fbe8bd0f5b5666eba1890d8def9036ec5eee350/portal/src/app/preview/journey-pdf-preview/journey-pdf-preview.component.scss#L6} */
/* 
  Pdf Preview Rules 
  - can have 1, 2, or 3 pages, for a max of 3 "phone" components
  - "messageSender" types take up 2 spaces (1 for the sms, 1 for the message)
      - they always start a new row
      - they can only be on the same page as 1 other non-"MessageSender" type
*/

class PdfPage {
  title: string;
  triggerId: string;
  clientTriggerId: string | null;
  workFlowDelayerId: string | null;
  messagePreviews: (MessageClass | Action)[];

  constructor(
    title: string = '',
    triggerId: string = '',
    clientTriggerId: string | null = null,
    workFlowDelayerId: string | null = null,
    messagePreviews: (MessageClass | Action)[] = [],
  ) {
    this.title = title;
    this.messagePreviews = messagePreviews;
    this.triggerId = triggerId;
    this.clientTriggerId = clientTriggerId;
    this.workFlowDelayerId = workFlowDelayerId;
  }

  public containsMessageSender() {
    return (
      _.filter(this.messagePreviews, (messagePreview) => {
        return messagePreview.type === 'MessageSender';
      }).length > 0
    );
  }

  public isFull() {
    return (
      (this.containsMessageSender() && this.messagePreviews.length === 2) ||
      this.messagePreviews.length === 3
    );
  }

  public addMessagePreview(messagePreview: MessageClass | Action) {
    this.messagePreviews.push(messagePreview);
  }
}

type PdfPageAnchor = {
  title: string /** text to appear on the anchor link - aka the title */;
  step: string /** first step of a page (MessageSender) */;
  pageId: string /** id of the page - used to find the element and scroll to view */;
  isWorkFlowDelayer: boolean /** if the page is a workFlowDelayer --> link is indented */;
};

@Component({
  selector:
    'app-journey-pdf-preview' /**  this selector is also being used in the @see {JourneyPdfExpandPreviewComponent} */,
  templateUrl: './journey-pdf-preview.component.html',
  styleUrls: ['./journey-pdf-preview.component.scss'],
  standalone: true,
  imports: [
    NgClass,
    PhoneComponent,
    JourneyArrowComponent,
    NewGenericPhoneComponent,
    AboutRelayPreviewReactContainerComponent,
    PrivacyPolicyPreviewReactContainerComponent,
    TermsAndConditionsPreviewReactContainerComponent,
    VerificationPreviewComponent,
    SetPasswordPagePreviewReactContainerComponent,
    PasswordPagePreviewComponent,
    MessageDialogComponent_1,
    ScreenSelectionDialogComponent_1,
    TimerPopupComponent,
    AsyncPipe,
    DatePipe,
  ],
})
export class JourneyPdfPreviewComponent implements OnInit {
  @ViewChild('messageDialogError', { static: true })
  errorDialog: MessageDialogComponent;
  @ViewChild('messageDialogProgress', { static: true })
  progressDialog: MessageDialogComponent;
  @ViewChild('screenSelectionDialog', { static: true })
  screenSelectionDialog: ScreenSelectionDialogComponent;
  @Input() messagePreviews: MessageClass[];
  @Input() productGroup: ProductGroup;
  @Input() journey: Journey;
  @Input() journeyContent: JourneyContent;
  @Input() previewMode: 'expanded' | 'collapsed' = 'collapsed';
  printStyleElement: HTMLStyleElement;
  _pages: PdfPage[];
  _pageAnchors: PdfPageAnchor[];
  hasIWCU: boolean;
  editMode: boolean = false;
  pdfBrandColor: string;
  twoWayDescText$: { [key: string]: Observable<string> } = {};
  additionalScreens: ScreenOption[];
  featureFlags = FeatureFlags;
  client: Client;
  smsAutoResponses: { title: string; message: MessageClass }[];
  verificationPreviewProps: VerificationPreviewProps;
  passwordPagesPreviewProps: PasswordPagesPreviewProps;

  constructor(
    private route: ActivatedRoute,
    private WirePreviewService: WirePreviewService,
    private pdfService: PdfService,
    private controlTagsService: ControlTagsService,
    private featureService: FeatureService,
    private journeyService: JourneyService,
    private sessionService: SessionService,
    private clientService: ClientService,
    private t2eService: T2eService,
  ) {}

  ngOnInit() {
    Background.previewOn();
    const journeyId = this.route.snapshot.params['journeyId'];

    this.client = this.sessionService.getCurrentUsersClient();

    this.WirePreviewService.getDataFromJourneyId(journeyId).subscribe(
      (data) => {
        this.journey = data.journey;
        this.productGroup = data.productGroup;
        this.hasIWCU = data.hasIWCU;
        this.journeyContent = this.journey.latestContent;
        this.messagePreviews = _.get(
          this.journey,
          'latestContent.components',
          [],
        );
        this._pages = this.generatePages();
        this._pageAnchors = this.generatePageAnchors();
        this.pdfBrandColor = this.productGroup.branding.color;
        this.loadAvailableScreens();
        this.populateSmsAutoResponses();
        this.verificationPreviewProps = resolveVerificationPreviewProps(
          this.client,
          this.productGroup,
          this.journey?.latestContent?.language,
        );
        this.passwordPagesPreviewProps = resolvePasswordPagesPreviewProps(
          this.client,
          this.productGroup,
          this.journey?.latestContent?.language,
        );
      },
      (error) => {
        this.errorDialog.showMessage(
          'Oops...there was an error getting the experience.',
        );
      },
    );

    window.onbeforeprint = () => {
      const { width, height } = this.calculatePageWidthAndHeight();

      const styles = `@page {
        size: ${width}px ${height}px;
      }`;

      this.printStyleElement = document.createElement('style');
      this.printStyleElement.textContent = styles;
      document.head.appendChild(this.printStyleElement);
    };
    window.onafterprint = () => {
      if (this.printStyleElement) {
        document.head.removeChild(this.printStyleElement);
      }
    };
  }

  populateSmsAutoResponses() {
    const responses = [];

    // Client config sms keywords
    if (this.client && this.client.sms_keywords) {
      const excludedKeywords = ['stopall', 'join'];
      for (const keyword in this.client.sms_keywords) {
        if (excludedKeywords.includes(keyword)) {
          continue;
        }
        for (const lang in this.client.sms_keywords[keyword]) {
          const langSuffixes = {
            en: '',
            es: ' (Spanish)',
            fr: ' (French)',
          };
          const suffix = langSuffixes[lang] || '';
          const title =
            keyword.charAt(0).toUpperCase() + keyword.slice(1) + suffix;
          const text =
            this.client.sms_keywords[keyword][lang].response_template;
          if (text) {
            const message = new MessageClass({ showSmsByDefault: true });
            message.type = 'MessageSender';
            message.sms = new SmsClass({ showSmsByDefault: true });
            message.sms.text = this.formatSmsResponseText(
              text,
              this.smsResponseValues(keyword, lang),
            );
            message.sms.is_shown = 'true';
            message.wire = new WireClass();
            message.wire.is_shown = 'false';
            responses.push({ title, message });
          }
        }
      }
    }

    // T2E keywords
    this.clientService.getClientShortCode(this.client.id).subscribe((resp) => {
      const shortcode = resp['shortcode'];
      this.t2eService
        .getAllKeywordsByShortcode(this.client.id, shortcode)
        .subscribe((keywords: T2EKeyword[]) => {
          const clientKeywords = keywords.filter(
            (kw) => kw.clientId === this.client.id,
          );
          clientKeywords.forEach((kw) => {
            const title = `${kw.keyword} (Custom)`;
            const message = new MessageClass({ showSmsByDefault: true });
            message.type = 'MessageSender';
            message.sms = new SmsClass({ showSmsByDefault: true });
            message.sms.text = this.formatSmsResponseText(kw.template, [
              this.smsResponseNameValue(),
            ]);
            message.sms.is_shown = 'true';
            message.wire = new WireClass();
            message.wire.is_shown = 'false';
            responses.push({ title, message });
          });
        });
    });

    this.smsAutoResponses = responses;
  }

  /**
   * Only the `help` keyword's response_template has a second `%s` (for the
   * product group's help text/help site) — see the tooltip in
   * client-inputs-new.component.html. Every other keyword just gets the name.
   */
  smsResponseValues(keyword: string, lang: string): string[] {
    const values = [this.smsResponseNameValue()];
    if (keyword === 'help') {
      values.push(this.smsHelpInstructionsValue(lang));
    }
    return values;
  }

  smsResponseNameValue(): string {
    const isAllProductGroups = this.journeyContent?.product_group === 'all';
    return (
      isAllProductGroups
        ? this.client?.company_name || ''
        : this.productGroup?.name || ''
    ).trim();
  }

  smsHelpInstructionsValue(lang: string): string {
    if (lang === 'es') {
      return (
        this.productGroup?.help_instructions_spanish ||
        this.productGroup?.help_instructions ||
        DEFAULT_HELP_INSTRUCTIONS
      );
    }
    return this.productGroup?.help_instructions || DEFAULT_HELP_INSTRUCTIONS;
  }

  formatSmsResponseText(template: string, values: string[]): string {
    let i = 0;
    return template.replace(/%s/g, () => values[i++] ?? '');
  }

  /**
   *
   * @returns { width: number; height: number } - the width and height of the page for printing
   */
  calculatePageWidthAndHeight(): { width: number; height: number } {
    const journeyPreviewDiv = document.getElementById('journey-pdf-preview');
    const pdfPages = document.getElementsByClassName('page');

    let pageHeight: number = defaultPageHeight;
    // Width of the page is applied to both expanded and collapsed modes, so we can calculate it by grabbing the first page's width
    let pageWidth: number =
      pdfPages.length > 0 ? pdfPages[0].clientWidth : defaultPageWidth;

    // if we are in expanded mode, we need to calculate the height of the page based on the whole content of the journey
    if (this.previewMode === 'expanded') {
      pageHeight = journeyPreviewDiv
        ? journeyPreviewDiv.clientHeight
        : defaultPageHeight;
    }
    return { width: pageWidth, height: pageHeight };
  }

  get showAdditionalScreensButton(): boolean {
    return this.featureService.checkFlag(
      this.featureFlags.pdf_additional_screen_preview,
    );
  }

  chooseAdditionalScreens() {
    this.screenSelectionDialog.showScreenSelection();
  }

  private loadAvailableScreens() {
    // Filter out screens that aren't applicable to this product group's config.
    // The feed view disclaimer screen is only relevant when the feed view
    // disclaimer is enabled for the product group; otherwise hide the checkbox.
    // TODO: Filter out other screens based on product group config (sign-up, verification, etc.. whether they are ENABLED)
    this.additionalScreens = DEFAULT_ADDITIONAL_SCREENS.filter((screen) => {
      if (screen.id === 'feed-view-disclaimer-screen') {
        return this.isFeedViewDisclaimerEnabled();
      }
      if (screen.id === 'verification-screen') {
        return this.isVerificationEnabled();
      }
      if (screen.id === 'about-menu-items-screen') {
        return this.isAboutMenuEnabled();
      }
      if (screen.id === 'password-pages-screen') {
        return this.isPasswordEnabled();
      }
      return true;
    });
  }

  private isFeedViewDisclaimerEnabled(): boolean {
    return this.productGroup?.disclaimer?.feed_view_enabled === 'true';
  }

  private isVerificationEnabled(): boolean {
    return this.client?.verification?.enabled === 'true';
  }

  private isAboutMenuEnabled(): boolean {
    return !this.productGroup?.hide_hamburger;
  }

  private isPasswordEnabled(): boolean {
    return this.productGroup?.password?.enabled ?? false;
  }

  onScreensSelected(selectedScreens: ScreenOption[]) {
    this.additionalScreens = [...selectedScreens];
  }

  requestPdf() {
    this.progressDialog.showMessage(
      'Generating your pdf.  This could take up to a minute.  Please wait...',
    );

    // the design system components use an "implicit" form of styling where
    // the sytles are not inserted into the DOM but modified in memory with
    // `insertRule`—we can retrieve these styles from memory with the following
    // code to then manually insert them into the DOM before we ship it over to
    // the PDF generation backend
    let styles = '';
    for (let i = 0; i < document.styleSheets.length; i++) {
      const styleSheet = document.styleSheets[i];

      // Will ONLY skip stylesheet that come from 3rd party service such as Bootstrap, fontawesome
      // and keep any inhouse styling such as app.scss(will be combine into styles.css file), styles.css
      if (styleSheet.href != null && !styleSheet.href.match(/\/styles.css/g)) {
        continue;
      }

      try {
        for (let j = 0; j < styleSheet.cssRules.length; j++) {
          const cssRule = styleSheet.cssRules[j];
          // only inject the design system styles (all of which start with `.ds-`)
          if (cssRule.cssText.match(/\.ds-/g)) {
            styles += `${cssRule.cssText}\n`;
          }
        }
      } catch (error) {
        console.info(error);
      }
    }
    const styleElement = document.createElement('style');
    styleElement.textContent = styles;
    document.head.appendChild(styleElement);

    const { width, height } = this.calculatePageWidthAndHeight();
    this.pdfService.requestPdf(this.journey.latestContent.name, {
      width,
      height,
    });

    this.pdfService.status.subscribe((status) => {
      if (status === 'idle') {
        this.progressDialog.cancelAction();
      }

      if (status === 'error') {
        this.progressDialog.cancelAction();
        this.errorDialog.showMessage(
          'Oops...there was an error generating your PDF.',
        );
      }
    });
  }

  getNewPageTitle(messagePreview) {
    const journeyTitle = this.journeyContent.name;
    if (
      messagePreview.type === 'APITrigger' &&
      (messagePreview.nickname || messagePreview.client_trigger_id)
    ) {
      return messagePreview.nickname
        ? `${journeyTitle} - ${messagePreview.nickname}`
        : `${journeyTitle}`;
    }
    if (messagePreview.type === 'WorkflowDelayer') {
      return `${journeyTitle} - ${messagePreview.interval_value} ${
        messagePreview.interval_type
      }${messagePreview.interval_value > 1 ? 's' : ''} later`;
    }
    return journeyTitle;
  }

  getNewPageTriggerAndClientTriggerId(messagePreview) {
    let trigger = null;
    let clientTrigger = null;
    if (messagePreview.type === 'APITrigger') {
      trigger = messagePreview.family;
      if (messagePreview.client_trigger_id)
        clientTrigger = messagePreview.client_trigger_id;
    }
    return { trigger, clientTrigger };
  }

  getWorkFlowDelayerId(messagePreview): string | null {
    let workFlowDelayerId = null;
    if (messagePreview.type === 'WorkflowDelayer') {
      workFlowDelayerId = messagePreview.name;
    }
    return workFlowDelayerId;
  }

  goToPageId(pageId: string) {
    document.getElementById(`${pageId}`).scrollIntoView();
  }

  actionDetailText(action): string | void {
    if (action.type === 'email') {
      return `<span  dir="auto">"${action.label}"</span> sends an email to ${action.value}`;
    }
    if (action.type === 'call') {
      return `<span  dir="auto">"${action.label}"</span> places a call to ${action.value}`;
    }
    if (action.type === 'hyperlink') {
      return `<span  dir="auto">"${action.label}"</span> sends user to <a href="${action.value}">${action.value}</a>`;
    }
    if (action.type === 'message') {
      return `<span  dir="auto">"${
        action.label
      }"</span> sends user to message ${this.matchingMessage(action)}`;
    }
    if (action.type === 'consent_upgrade') {
      return `<span  dir="auto">"${action.label}"</span> is an in wire consent upgrade form.`;
    }
    if (action.type === 'collapsible') {
      return `<span  dir="auto">"${action.label}"</span> is a collapsible content area.`;
    }
    if (action.type === 'form_info_capture') {
      return `<span  dir="auto">"${action.label}"</span> opens this form: <a href="/powerupsform/${action.value}">${action.value}</a>`;
    }
    if (action.type === 'disclaimer' && action.show_disclaimer) {
      return 'Disclaimer message.';
    }
  }

  twoWayActionDetailText(action): Observable<string> {
    if (!this.twoWayDescText$[action.id]) {
      this.twoWayDescText$[action.id] = this.controlTagsService
        .getTag(action.value)
        .pipe(
          flatMap((tag) => {
            return of(
              `<span  dir="auto">"${action.label}"</span> initiates a conversation tagged "${tag.tag_name}"`,
            );
          }),
        )
        .pipe(
          catchError((err) => {
            return of(
              `<span  dir="auto">"${action.label}"</span> initiates a conversation`,
            );
          }),
        );
    }

    return this.twoWayDescText$[action.id];
  }

  matchingMessage(action) {
    const matchingMessage = this.journeyContent.components.filter(
      (component) => {
        return component.name === action.value;
      },
    )[0];

    return matchingMessage ? matchingMessage.step : 'unknown';
  }

  /* 
    All possible message types: 
   'APITrigger', 'WorkflowDelayer', 'WorkflowEnder', 'MessageSender', 'MessageResponse'
  */
  isDisplayableMessageComponent(message: MessageClass | Action) {
    if (message instanceof Action && message.type === 'form_info_capture') {
      return true;
    } else if (message instanceof MessageClass) {
      return (
        (message.type === 'MessageSender' ||
          message.type === 'MessageResponse') &&
        (message.sms.is_shown === 'true' || message.wire.is_shown === 'true')
      );
    }
  }

  isDisplayableWireMessage(message: MessageClass | Action) {
    if (message instanceof Action && message.type === 'form_info_capture') {
      return true;
    } else if (message instanceof MessageClass) {
      return (
        (message.type === 'MessageSender' ||
          message.type === 'MessageResponse') &&
        message.wire.is_shown === 'true'
      );
    }
  }

  isSmsMessage(message: MessageClass) {
    return message.type === 'MessageSender' && message.sms.is_shown === 'true';
  }

  get firstDisplayableWireMessage(): MessageClass | undefined {
    return this.messagePreviews?.find((m) =>
      this.isDisplayableWireMessage(m),
    ) as MessageClass;
  }

  get pages(): PdfPage[] {
    return this._pages;
  }

  get pageAnchors(): PdfPageAnchor[] {
    return this._pageAnchors;
  }

  generatePages(): PdfPage[] {
    if (_.isEmpty(this.journey)) {
      return [];
    }

    const pdfPages: PdfPage[] = [];
    let pageNumber = 0;
    let pageTitle = '';
    let triggerId = '';
    let clientTriggerId = null;
    let workFlowDelayerId = null;

    for (let i = 0; i < this.messagePreviews.length; i++) {
      const currentMessagePreview = this.messagePreviews[i];
      const messageActions =
        (currentMessagePreview.formActions &&
          currentMessagePreview.formActions()) ||
        [];

      const allPhones = [
        currentMessagePreview,
        ...messageActions.map((item) => new Action(item)),
      ];

      let currentPage = pdfPages[pageNumber];

      if (!this.isDisplayableMessageComponent(currentMessagePreview)) {
        pageTitle = this.getNewPageTitle(currentMessagePreview);
        const { trigger, clientTrigger } =
          this.getNewPageTriggerAndClientTriggerId(currentMessagePreview);
        triggerId = trigger;
        clientTriggerId = clientTrigger;
        workFlowDelayerId = this.getWorkFlowDelayerId(currentMessagePreview);
      }

      allPhones.forEach((item) => {
        if (this.isDisplayableMessageComponent(item)) {
          if (
            pdfPages.length > 0 &&
            (currentPage.isFull() || item.type === 'MessageSender')
          ) {
            pageNumber += 1;
          }

          if (!pdfPages[pageNumber]) {
            currentPage = pdfPages[pageNumber] = new PdfPage(
              pageTitle,
              triggerId,
              clientTriggerId,
              workFlowDelayerId,
            );
          }
          pdfPages[pageNumber].addMessagePreview(item);
        }
      });
    }

    return pdfPages;
  }

  // return the id of the page and the index of the message preview
  // the id is either the triggerId or the workFlowDelayerId
  getPDFPageId(page: PdfPage, index: number) {
    return `${page.triggerId || page.workFlowDelayerId}--${index}`;
  }

  /**
   * Each page in @see {pages} has either triggerId or workFlowDelayerId (but not both) and these ids can be repeated.
   * This function generates an array of unique page anchors based on the triggerId or workFlowDelayerId.
   * If a page has a workFlowDelayerId, it is tagged with @see {isWorkFlowDelayer} so the link can be indent.
   * @see {getPDFPageId} is used to generate the pageId and also to link the page to the anchor
   * @returns {PdfPageAnchor[]} - an array of page anchors
   */
  generatePageAnchors(): PdfPageAnchor[] {
    const uniqueTriggerOrWorkflowDelayId: string[] = [];
    const pageAnchors: PdfPageAnchor[] = [];
    for (let i = 0; i < this.pages.length; i++) {
      const triggerOrWorkflowId =
        this.pages[i].triggerId || this.pages[i].workFlowDelayerId;
      if (!uniqueTriggerOrWorkflowDelayId.includes(triggerOrWorkflowId)) {
        uniqueTriggerOrWorkflowDelayId.push(triggerOrWorkflowId);

        let step = '';
        const pageId = this.getPDFPageId(this.pages[i], i);
        const title = this.pages[i].title;
        const isWorkFlowDelayer = !!this.pages[i].workFlowDelayerId;
        if (this.pages[i].messagePreviews.length > 0) {
          const firstMessagePreview = this.pages[i].messagePreviews[0];
          if (firstMessagePreview instanceof MessageClass) {
            step = firstMessagePreview.step;
          }
        }
        pageAnchors.push({ title, pageId, isWorkFlowDelayer, step });
      }
    }
    return pageAnchors;
  }

  /*
    1. came to this via trial and error - without it you get a blank page at the end
   */
  documentHeight() {
    if (!this._pages) {
      return;
    }
    const pageHeight = 8 * 1.5;
    return `${this._pages.length * pageHeight}in`;
  }

  toggleEditMode() {
    this.editMode = !this.editMode;
    if (this.editMode) {
      document.designMode = 'on';
    } else {
      document.designMode = 'off';
    }
  }

  viewExpandedPreview() {
    this.journeyService.pdfExpandPreview(this.journey);
  }

  viewCollapsedPreview() {
    this.journeyService.pdfPreview(this.journey);
  }

  close() {
    window.close();
  }

  closeDialog() {
    this.errorDialog.cancelAction();
  }
}
